// Ported from dev-scrips-pm-react src/billing/components/invoice-pending-order-row.tsx (PROD-3312 appointment Billing tab,
// step 6). PM primitives come from the PROD-3310 billing bridge. Differences from pm-react:
//   - `cn(...)` from the PM utils is a plain template string here (one conditional class).
//   - "Set a price first" is plain text here. pm-react links it to its service-list page; the PA has no such route
//     (PROD-3312 gap: the fee-schedule page lives in pm-react only), so the link waits on an owner decision.
import { Plus } from 'lucide-react';
import {
  BridgeButton as Button,
  BridgeCheckbox as Checkbox,
  BridgeSelect as Select,
  BridgeStatusChip,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/billing/bridge';
import type { InvoiceOrderServiceCandidate } from '@/shared/types/billing';
import { formatMoney } from '@/shared/lib/money';
import {
  codeLabel,
  groupFee,
  groupPackageDiscount,
  groupReadiness,
  labLineContext,
  notClaimableReason,
  lineFee,
  type OrderGroup,
  type ServiceChoices,
} from '@/billing/lib/pending-orders';
import { DismissOrdersPopover, type DismissChoice } from './invoice-pending-orders-dialogs';

function candidateLabel(c: InvoiceOrderServiceCandidate | undefined): string | undefined {
  return c ? `${c.description} · ${c.code} · ${formatMoney(c.fee)}` : undefined;
}

interface PendingOrderRowProps {
  group: OrderGroup;
  choices: ServiceChoices;
  /** Draft + `canEdit`: shows the checkbox, the picker and the row actions. */
  editable: boolean;
  busy: boolean;
  selected: boolean;
  onToggle: (key: string, selected: boolean) => void;
  onChoose: (sourceOrderKey: string, serviceId: string) => void;
  onAdd: (group: OrderGroup) => void;
  /** NoPrice: open the add-service picker for this order (PROD-2678). */
  onPickService: (group: OrderGroup) => void;
  onDismiss: (group: OrderGroup, choice: DismissChoice) => Promise<boolean>;
}

/**
 * One pending order (canvas board 1). A vaccine with an administration line is
 * one row that adds two invoice lines, and says so.
 */
export function PendingOrderRow({
  group,
  choices,
  editable,
  busy,
  selected,
  onToggle,
  onChoose,
  onAdd,
  onPickService,
  onDismiss,
}: PendingOrderRowProps) {
  const { primary, lines } = group;
  const readiness = groupReadiness(group, choices);
  const fee = groupFee(group, choices);
  const twoLines = lines.length > 1;
  const unsigned = lines.some((l) => !l.signed);
  const diagnoses = primary.diagnoses;
  const ambiguous = lines.filter((l) => l.priceState === 'Ambiguous');
  const labContext = labLineContext(primary);
  const packageDiscount = groupPackageDiscount(group);
  const notClaimable = notClaimableReason(group);

  return (
    <tr className={`border-b border-border-subtle last:border-0 align-middle${selected ? ' bg-grey-100' : ''}`}>
      {editable && (
        <td className="px-3 py-2 w-4">
          <Checkbox
            aria-label={`Select ${primary.display}`}
            checked={selected}
            // PROD-2678: every pending row can be ticked (Dismiss applies to all
            // of them); bulk Add takes only the ready ones.
            disabled={busy}
            onCheckedChange={(v) => onToggle(group.key, Boolean(v))}
          />
        </td>
      )}
      <td className="px-3 py-2 text-text-primary min-w-0">
        <div className="font-medium break-words">{primary.display}</div>
        <div className="text-xs text-text-muted tabular-nums">
          {codeLabel(primary)}
          {/* The Type column folds into this line at narrow widths (calendar Billing tab). */}
          <span className="@xl:hidden"> · {primary.orderType}</span>
        </div>
        {/* So do Order status and Ordered by (PROD-2630). */}
        {(primary.orderStatus || primary.orderedBy?.name) && (
          <div data-testid="order-meta-subline" className="text-xs text-text-muted @3xl:hidden">
            {[primary.orderStatus, primary.orderedBy?.name].filter(Boolean).join(' · ')}
          </div>
        )}
        {labContext && <div className="text-xs text-text-muted">{labContext}</div>}
        {twoLines && <div className="text-xs text-text-muted">Adds 2 lines: product + administration</div>}
        {diagnoses.length > 0 && (
          <div className="text-xs text-text-muted break-words">
            Dx: {diagnoses.map((d) => [d.code, d.display].filter(Boolean).join(' ')).join('; ')}
          </div>
        )}
        {(unsigned || readiness === 'noPrice' || notClaimable) && (
          <div className="mt-1 flex flex-wrap gap-2">
            {unsigned && (
              <BridgeStatusChip semantic="warning" tone="soft" size="xs">
                Not signed
              </BridgeStatusChip>
            )}
            {readiness === 'noPrice' && (
              <BridgeStatusChip semantic="warning" tone="soft" size="xs">
                No price in fee schedule
              </BridgeStatusChip>
            )}
            {/* PROD-2858: claims carry CPT codes only; staff decide at Add. */}
            {notClaimable && (
              <BridgeStatusChip semantic="warning" tone="soft" size="xs">
                {notClaimable}
              </BridgeStatusChip>
            )}
          </div>
        )}
      </td>
      <td className="hidden px-3 py-2 text-text-secondary @xl:table-cell">{primary.orderType}</td>
      {/* PROD-2630: the order's own status and who ordered it. The chip is neutral
          grey for every status on purpose (canvas board 1). */}
      <td className="hidden px-3 py-2 @3xl:table-cell">
        {primary.orderStatus ? (
          <BridgeStatusChip semantic="neutral" tone="soft" size="xs">
            {primary.orderStatus}
          </BridgeStatusChip>
        ) : (
          <span className="text-text-hint">—</span>
        )}
      </td>
      <td className="hidden px-3 py-2 text-text-secondary @3xl:table-cell">
        {primary.orderedBy?.name ?? <span className="text-text-hint">—</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {ambiguous.length > 0 && editable ? (
          <div className="flex flex-col items-end gap-1">
            {ambiguous.map((line) => (
              <Select
                key={line.sourceOrderKey}
                value={choices[line.sourceOrderKey] ?? null}
                onValueChange={(v) => v && onChoose(line.sourceOrderKey, String(v))}
              >
                <SelectTrigger size="sm" aria-label={`Choose service for ${line.display}`} className="max-w-56">
                  <SelectValue placeholder="Choose service" />
                </SelectTrigger>
                <SelectContent>
                  {line.serviceCandidates.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {candidateLabel(c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ))}
            {fee !== null && <span className="text-text-secondary">{formatMoney(fee)}</span>}
          </div>
        ) : fee !== null ? (
          <>
            <span className="text-text-secondary">{formatMoney(fee)}</span>
            {/* PROD-2858: a self-pay clinic panel line's share of the package price. */}
            {packageDiscount !== null && (
              <div className="text-xs text-text-muted">Package discount -{formatMoney(packageDiscount)}</div>
            )}
            {twoLines && (
              <div className="text-xs text-text-muted">
                {lines
                  .map((l) => {
                    const f = formatMoney(lineFee(l, choices));
                    return l.chargeRole === 'VaccineAdministration' ? `${f} admin (${l.serviceCode ?? '90471'})` : f;
                  })
                  .join(' + ')}
              </div>
            )}
          </>
        ) : (
          <span className="text-text-hint">{readiness === 'needsChoice' ? 'Choose service' : '—'}</span>
        )}
      </td>
      {editable && (
        <td className="px-3 py-2">
          <div className="flex flex-wrap items-start justify-end gap-1">
            <DismissOrdersPopover
              title={`Dismiss ${primary.display} from this invoice?`}
              pending={busy}
              onConfirm={(choice) => onDismiss(group, choice)}
              trigger={
                <Button variant="ghost" size="sm" disabled={busy} className="text-text-secondary">
                  Dismiss
                </Button>
              }
            />
            <div className="flex flex-col items-end gap-1">
              {readiness === 'noPrice' ? (
                // PROD-2678: no fee-schedule price, so the biller picks the
                // service to bill it as; picking it is the confirmation.
                <Button variant="outline" size="sm" disabled={busy} onClick={() => onPickService(group)}>
                  <Plus aria-hidden /> Choose service
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy || readiness !== 'ready'}
                  onClick={() => onAdd(group)}
                >
                  <Plus aria-hidden /> Add to invoice
                </Button>
              )}
              {readiness === 'noPrice' && (
                // Plain text, not a link: the PA has no fee-schedule route yet; the link waits on an owner decision (PROD-3312).
                <span className="text-xs text-text-muted">Set a price first</span>
              )}
            </div>
          </div>
        </td>
      )}
    </tr>
  );
}
